// §9.6 / §18 F6 — the shell-drawn error window for an app whose origin is down.
//
// PLAN §5.4 is specific about what this replaces: *"a Hangar-drawn error window … rather than a
// blank iframe"*. So the window opens, keeps its frame and its bar entry, and the shell draws the
// message and the three actions into the content slot — no iframe is ever created for a launch that
// could not reach the origin.
//
// It is a portal into `.win-content`, the same slot an app would have used, which keeps §5.7 intact:
// the host element is still created once and removed only by the close.
import { createPortal } from 'react-dom';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import type { HostSlots } from './AppHost';

export interface FailedApp {
  winId: string;
  appId: string;
  name: string;
  /** §9.6 "Open in a tab" — the manifest entry, opened as an ordinary page. */
  entry: string;
}

export interface AppErrorLayerProps {
  failed: ReadonlyMap<string, FailedApp>;
  slots: ReadonlyMap<string, HostSlots>;
  /** Re-probe. The host mounts the frame itself if the origin came back (§9.6). */
  onRetry: (instanceKey: string) => void;
  onUninstall: (instanceKey: string) => void;
}

function AppErrorPanel({
  app,
  onRetry,
  onUninstall,
}: {
  app: FailedApp;
  onRetry: () => void;
  onUninstall: () => void;
}) {
  const kernel = useKernel();
  return (
    <div className="app-error" role="alert" data-testid={`app-error-${app.appId}`}>
      <span
        className="app-error-icon"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(app.appId, 48) }}
      />
      <p className="app-error-message" data-testid="app-error-message">
        {COPY.appNotResponding(app.name)}
      </p>
      <div className="app-error-actions">
        <button type="button" data-testid="app-error-retry" onClick={onRetry}>
          {COPY.appRetry}
        </button>
        <button
          type="button"
          data-testid="app-error-open-tab"
          // `noopener` matters here more than usual: this is the one place the shell hands a
          // *failing* external origin a window, and it must not get a handle back on ours.
          onClick={() => window.open(app.entry, '_blank', 'noopener,noreferrer')}
        >
          {COPY.appOpenInTab}
        </button>
        <button type="button" data-testid="app-error-uninstall" onClick={onUninstall}>
          {COPY.appUninstall}
        </button>
      </div>
    </div>
  );
}

export function AppErrorLayer({ failed, slots, onRetry, onUninstall }: AppErrorLayerProps) {
  return (
    <>
      {[...failed].map(([instanceKey, app]) => {
        const slot = slots.get(instanceKey);
        if (!slot) return null;
        return createPortal(
          <AppErrorPanel
            app={app}
            onRetry={() => onRetry(instanceKey)}
            onUninstall={() => onUninstall(instanceKey)}
          />,
          slot.content,
          instanceKey,
        );
      })}
    </>
  );
}
